import React, { useEffect, useCallback, useState } from 'react'
import { Stack, Box, useDisclosure, Text } from '@chakra-ui/react'
import { Button } from '@apex/component-library'
import { FromAccount } from '../FromAccount'
import { TransferAmount } from '../TransferAmount'
import { useFormikContext } from 'formik'
import { WireTransferValues, BeneficiaryTypes, LocationOptions } from '../../containers/WireTransfers/Types'
import { withFormikDevtools } from 'formik-devtools-extension'
import { useFormatNumber } from '../../utils/intlHelpers'
import numberInputFormatter from '../../utils/inputFormatters'
import { getAllAccountNames } from '../../utils/getAccountNames'
import { WireSelectBeneficiary } from '../WireSelectBeneficiary'
import WireThirdPartyForm from '../WireThirdPartyForm'
import { TermsOfUse } from '../TermsOfUse'
import WireBankForm from '../WireBankForm'
import WireIntermediaryAccount from '../WireIntermediaryAccount'
import TransferFrequency from '../TransferFrequency'
import WireTransferPreviewModal from '../WireTransferPreviewModal'
import { SearchAccountsResponsePayload, DistributionReasonInfo, RetirementConstraintsResponse } from '@Types'
import { delayFn, formatFromAccount, MAX_FORM_WIDTH } from '../../utils'
import formatPayload from '../../containers/WireTransfers/formatPayload'
import useWireSubmission from '../../api/useWireSubmission'
import { CountryItem } from '../../reducers/types'
import { useCreateScheduleTransfer } from '../../api/useCreateScheduledTransfer'
import useGetSchedules from '../../api/useGetSchedules'
import useGetRecentCashActivity from '../../api/useGetRecentCashActivity'
import IraDistributionForm from '../IraDistributionForm'
import TransferFees from '../TransferFees'
import { FeeTransferType, TransferFeeDescriptionType } from '../TransferFees/types'

export interface WireTransferFormProps {
  countryList: CountryItem[]
  countryListLoading: boolean
  countryListFailedMessage?: string
  countryAutoSelectDataSet: { value: string }[]
  account: SearchAccountsResponsePayload
  hasSubmitPermission: boolean
  constraintData: RetirementConstraintsResponse
  amountAvailable: number
  getCountryList: () => void
}

const WireTransferForm = ({
  countryListLoading,
  countryListFailedMessage,
  countryAutoSelectDataSet,
  countryList,
  account,
  hasSubmitPermission,
  constraintData,
  amountAvailable,
  getCountryList
}: WireTransferFormProps) => {
  const numberFormatter = useFormatNumber()

  const formik = useFormikContext<WireTransferValues>()
  withFormikDevtools(formik)

  const fromAccountDisplay = formatFromAccount(account.accountNumber, amountAvailable, account.accountTitle)

  const allAccountNames = getAllAccountNames(account.accountTitle, account.accountNames)

  const [selectedReason, setSelectedReason] = useState<DistributionReasonInfo | null>()
  const [applicableFees, setApplicableFees] = useState<TransferFeeDescriptionType[]>([])

  const handleSelectReason = (reason: DistributionReasonInfo | null) => {
    setSelectedReason(reason)
  }

  const { isOpen, onOpen, onClose } = useDisclosure()

  const clearTransferAmount = () => {
    formik.setFieldValue('transferAmountDisplay', '')
    formik.setFieldValue('transferAmountRaw', 0)
  }

  const clearTransferAmountMemo = useCallback(clearTransferAmount, [])

  const { refetch } = useGetRecentCashActivity(account.accountNumber)
  const { refetch: refetchSchedulesData } = useGetSchedules(account.accountNumber)

  const submitWireTransfer = useWireSubmission({
    onSuccess: () => {
      delayFn(refetch)
      formik.resetForm()
      setSelectedReason(null)
      formik.validateForm()
    },
    onSettled: () => {
      onClose()
    },
    amountAvailable,
    amount: formik.values.transferAmountRaw,
    isFullDisbursement: formik.values.totalBalanceCheckbox
  })

  useEffect(() => {
    if (!countryList.length) {
      getCountryList()
    }
  }, [countryList, getCountryList])

  const handleSubmitSuccess = () => {
    formik.resetForm()
    formik.validateForm()
  }

  const handleOnSettle = () => {
    refetchSchedulesData()
    onClose()
  }

  const submitWireScheduledTransfer = useCreateScheduleTransfer('wires', 'withdrawals', {
    onSuccess: handleSubmitSuccess,
    onSettled: handleOnSettle
  })

  const onSubmit = () => {
    const isScheduledTransfer =
      formik.values.transferRecurrence === 'RECURRING' || formik.values.whenToSend === 'IN_THE_FUTURE'

    if (isScheduledTransfer) {
      submitWireScheduledTransfer.mutate(formatPayload(formik.values, countryList, isScheduledTransfer))
    } else {
      submitWireTransfer.mutate(formatPayload(formik.values, countryList, isScheduledTransfer))
    }
  }

  useEffect(() => {
    if (formik.values.totalBalanceCheckbox) {
      clearTransferAmountMemo()
    }
  }, [formik.values.totalBalanceCheckbox, clearTransferAmountMemo])

  useEffect(() => {
    let applicableFees: TransferFeeDescriptionType[] = []
    if (formik.values.bankDestination === LocationOptions.Domestic) {
      applicableFees.push('DOMESTIC_WIRE')
    } else {
      applicableFees.push('INTERNATIONAL_WIRE')
    }
    if (constraintData.retirementAccount && formik.values.totalBalanceCheckbox) {
      applicableFees.push('CLOSING')
    }
    setApplicableFees(applicableFees as TransferFeeDescriptionType[])
  }, [formik.values.bankDestination, constraintData.retirementAccount, formik.values.totalBalanceCheckbox])

  return (
    <>
      <Box width='100%' maxW={MAX_FORM_WIDTH} marginTop={6} marginBottom={6}>
        <FromAccount displayedValue={fromAccountDisplay} />
      </Box>
      <Box as='form' marginTop={4} pb={4} maxW={MAX_FORM_WIDTH} w='100%'>
        <Stack width='100%' spacing={4}>
          <TransferAmount
            value={formik.values.transferAmountDisplay}
            isDisabled={formik.values.totalBalanceCheckbox}
            errorMessage={formik.errors.transferAmountRaw}
            handleBlur={formik.handleBlur}
            handleAmountChange={(e) => {
              formik.setValues({
                ...formik.values,
                transferAmountDisplay: numberInputFormatter(e.target.value, numberFormatter).display,
                transferAmountRaw: numberInputFormatter(e.target.value, numberFormatter).raw as number
              })
            }}
            isInvalid={!!formik.errors.transferAmountRaw && formik.touched.transferAmountRaw}
            totalBalanceChecked={formik.values.totalBalanceCheckbox}
            handleCheckboxChange={formik.handleChange}
          />
          {!!formik.errors.hasTotalDistributionError && (
            <Text color={'red.500'}>{formik.errors.hasTotalDistributionError}</Text>
          )}
          <WireSelectBeneficiary
            setFieldValue={formik.setFieldValue}
            selectedType={formik.values.beneficiaryType}
            accountName={allAccountNames}
            name={formik.values.beneficiaryName}
            accountNum={formik.values.beneficiaryAccountNumOrIBAN}
            errors={formik.errors}
            formikTouched={formik.touched}
            formikBlur={formik.handleBlur}
          />
          {formik.values.beneficiaryType === BeneficiaryTypes.ThirdParty && (
            <WireThirdPartyForm
              formikValues={formik.values}
              formikTouched={formik.touched}
              formikBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              errors={formik.errors}
              setTouched={formik.setTouched}
              countryList={countryAutoSelectDataSet}
              countryListLoading={countryListLoading}
              countryListFailedMessage={countryListFailedMessage}
            />
          )}
          <WireBankForm
            formikValues={formik.values}
            formikTouched={formik.touched}
            formikBlur={formik.handleBlur}
            setFieldValue={formik.setFieldValue}
            errors={formik.errors}
            setTouched={formik.setTouched}
            countryList={countryAutoSelectDataSet}
            countryListLoading={countryListLoading}
            countryListFailedMessage={countryListFailedMessage}
          />
          {formik.values.bankUseIntermediary && (
            <WireIntermediaryAccount
              formikValues={formik.values}
              formikTouched={formik.touched}
              formikBlur={formik.handleBlur}
              setFieldValue={formik.setFieldValue}
              errors={formik.errors}
              setTouched={formik.setTouched}
              countryList={countryAutoSelectDataSet}
              countryListLoading={countryListLoading}
              countryListFailedMessage={countryListFailedMessage}
            />
          )}
          {constraintData?.retirementAccount ? (
            <IraDistributionForm
              formikSetValues={formik.setValues}
              formikValues={formik.values}
              formikBlur={formik.handleBlur}
              formikTouched={formik.touched}
              setFieldValue={formik.setFieldValue}
              setTouched={formik.setTouched}
              errors={formik.errors}
              selectedReason={selectedReason}
              handleSelectReason={handleSelectReason}
              distributionReasonOptions={constraintData.distributionConstraints?.validDistributionReasons}
              setFieldTouched={formik.setFieldTouched}
            />
          ) : null}

          <TransferFrequency
            selectedRecurrence={formik.values.transferRecurrence}
            selectedFrequency={formik.values.transferFrequency}
            selectedWhenToSend={formik.values.whenToSend}
            selectedScheduledDate={formik.values.transferScheduledDate}
            selectedStartDate={formik.values.transferStartDate}
            numberOfTransfers={formik.values.numberOfTransfers}
            isTotalBalanceTransfer={formik.values.totalBalanceCheckbox}
          />
          <TransferFees
            accountNumber={account.accountNumber}
            feeTransferType={FeeTransferType.wireWithdrawal}
            applicableFees={applicableFees}
          />
          <TermsOfUse isChecked={formik.values.termsOfUse} onChange={formik.handleChange} />
          <Box paddingTop={'1.5em'}>
            <Button variant='primary' data-test-id='wires-next-btn' isDisabled={!formik.isValid} onClick={onOpen}>
              Next
            </Button>
          </Box>
          <WireTransferPreviewModal
            formikValues={formik.values}
            account={account}
            accountName={allAccountNames}
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={submitWireTransfer.isLoading}
            fromAccount={fromAccountDisplay}
            hasSubmitPermission={hasSubmitPermission}
            amountAvailable={amountAvailable}
          />
        </Stack>
      </Box>
    </>
  )
}

export default WireTransferForm
